/**
 * LineageMiniMap — lane-based genealogy of a selected graph node.
 *
 * Cycle-105.8 sprint-3 (S3, g231) · FR-E-4 task 231.4 — the single novel build.
 * Ports the canonical design handoff
 * (`output/design_reference/design_handoff_echelon_2026_04_26/environment/detail.jsx:332-939`,
 * the `MiniMap` function) to TypeScript, driven by the REAL D3 graph
 * (`D3GraphNode[]` / `D3GraphEdge[]`) rather than the handoff's mock data.
 *
 * Vertical time axis, narrow fixed lanes. Walks up to ~10 generations of
 * ancestry and one of descendants. The primary line of descent ("trunk")
 * takes the center lane and gets the accent edges + a travelling pulse;
 * branches alternate outward. Per-row overflow collapses to a "+N" badge;
 * off-panel ancestry/descendants render as dashed ghost stubs.
 *
 * Visual language is reused from the canvas — node colours/shapes come from
 * `NODE_CLASSES` (D3GraphCanvas), so the MiniMap reads identically to the
 * force graph (no invented visual language). Honest empty state when the
 * selected node has no lineage.
 */

import { useLayoutEffect, useRef, useState, type ReactElement } from 'react';
import type { D3GraphNode, D3GraphEdge } from './graph/D3GraphCanvas';
import { NODE_CLASSES } from './graph/D3GraphCanvas';

// Status → fill colour. Values mirror the existing canvas / FloatingInspector
// palette (oklch tokens already used across the Environment surface).
const STATUS_DOT: Record<string, string> = {
  idle: 'oklch(0.380 0.007 265)',
  running: 'oklch(0.530 0.205 295)',
  active: 'oklch(0.530 0.205 295)',
  completed: 'oklch(0.568 0.160 152)',
  verified: 'oklch(0.688 0.165 72)',
  certified: 'oklch(0.688 0.165 72)',
  failed: 'oklch(0.615 0.208 22)',
  stale: 'oklch(0.480 0.007 265)',
};

function typeColour(type: string | undefined): string {
  return NODE_CLASSES[type ?? '']?.color ?? 'oklch(0.480 0.007 265)';
}
function statusFill(status: string | undefined): string {
  return STATUS_DOT[status ?? 'idle'] ?? 'oklch(0.480 0.007 265)';
}
function edgeId(end: string | D3GraphNode): string {
  return typeof end === 'string' ? end : end.id;
}

export interface LineageMiniMapProps {
  /** id of the currently-selected node (the leaf of its lineage chain). */
  nodeId: string;
  /** the selected node — used only for the accent colour. */
  node: D3GraphNode | null;
  nodes: D3GraphNode[];
  edges: D3GraphEdge[];
  onSelect?: (id: string) => void;
}

export function LineageMiniMap({ nodeId, node, nodes, edges, onSelect }: LineageMiniMapProps) {
  // Hooks must run unconditionally (rules of hooks) — measure the container.
  const containerRef = useRef<HTMLDivElement>(null);
  const [availH, setAvailH] = useState(520);
  const [availW, setAvailW] = useState(340);
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const h = e.contentRect.height;
        const w = e.contentRect.width;
        if (h > 0) setAvailH(h);
        if (w > 0) setAvailW(w);
      }
    });
    ro.observe(el);
    setAvailH(el.clientHeight || 520);
    setAvailW(el.clientWidth || 340);
    return () => ro.disconnect();
  }, []);

  if (!nodes.length) {
    return <div className="p-4 text-[12px] text-[var(--e-text-muted)]">No lineage.</div>;
  }
  const selected = nodes.find((n) => n.id === nodeId);
  if (!selected) {
    return <div className="p-4 text-[12px] text-[var(--e-text-muted)]">No selection.</div>;
  }

  // ── Parent / child adjacency ──
  const parentsOf: Record<string, string[]> = {};
  const childrenOf: Record<string, string[]> = {};
  edges.forEach((e) => {
    const from = edgeId(e.source);
    const to = edgeId(e.target);
    (childrenOf[from] = childrenOf[from] || []).push(to);
    (parentsOf[to] = parentsOf[to] || []).push(from);
  });

  // ── Generation walk (root-down tree rooted above the selected leaf) ──
  const MAX_UP = 10;
  const MAX_DOWN = 1;
  const MAX_NODES = 60;
  const MAX_PER_ROW = 5;

  const genOf: Record<string, number> = { [nodeId]: 0 };
  const order: string[] = [nodeId];

  // Upstream
  {
    let frontier = [nodeId];
    for (let g = 1; g <= MAX_UP && order.length < MAX_NODES; g++) {
      const next: string[] = [];
      for (const id of frontier) {
        for (const p of parentsOf[id] || []) {
          if (genOf[p] == null) {
            genOf[p] = -g;
            order.push(p);
            next.push(p);
            if (order.length >= MAX_NODES) break;
          }
        }
        if (order.length >= MAX_NODES) break;
      }
      if (!next.length) break;
      frontier = next;
    }
  }
  // Downstream
  {
    let frontier = [nodeId];
    for (let g = 1; g <= MAX_DOWN && order.length < MAX_NODES; g++) {
      const next: string[] = [];
      for (const id of frontier) {
        for (const c of childrenOf[id] || []) {
          if (genOf[c] == null) {
            genOf[c] = g;
            order.push(c);
            next.push(c);
            if (order.length >= MAX_NODES) break;
          }
        }
        if (order.length >= MAX_NODES) break;
      }
      if (!next.length) break;
      frontier = next;
    }
  }

  // Group by generation row
  const gens: Record<number, string[]> = {};
  order.forEach((id) => {
    (gens[genOf[id]] = gens[genOf[id]] || []).push(id);
  });
  let genKeys = Object.keys(gens).map(Number).sort((a, b) => a - b);

  // Per-row overflow — demote excess siblings to a "+N" count
  const rowOverflow: Record<number, number> = {};
  genKeys.forEach((g) => {
    const row = gens[g];
    if (row.length <= MAX_PER_ROW) return;
    const sorted = [...row].sort((a, b) => {
      const ta = a === nodeId ? 0 : 1;
      const tb = b === nodeId ? 0 : 1;
      if (ta !== tb) return ta - tb;
      return a.localeCompare(b);
    });
    const keep = sorted.slice(0, MAX_PER_ROW);
    const drop = sorted.slice(MAX_PER_ROW);
    const dropSet = new Set(drop);
    gens[g] = row.filter((id) => !dropSet.has(id));
    drop.forEach((id) => {
      delete genOf[id];
    });
    rowOverflow[g] = drop.length;
    void keep;
  });

  // Primary trunk — walk up via the first parent, down via the first child.
  const trunk = new Set<string>([nodeId]);
  {
    let cur = nodeId;
    for (let i = 0; i < MAX_UP * 2; i++) {
      const ps = parentsOf[cur] || [];
      if (!ps.length) break;
      const p = ps[0];
      if (trunk.has(p)) break;
      trunk.add(p);
      cur = p;
    }
  }
  {
    let cur = nodeId;
    for (let i = 0; i < MAX_DOWN * 2; i++) {
      const cs = childrenOf[cur] || [];
      if (!cs.length) break;
      const c = cs[0];
      if (trunk.has(c)) break;
      trunk.add(c);
      cur = c;
    }
  }

  // ── Lane assignment — center lane = trunk; branches alternate outward ──
  const LANES = 7;
  const CENTER = Math.floor(LANES / 2);
  const lane: Record<string, number> = {};

  genKeys.forEach((g) => {
    for (const id of gens[g]) if (trunk.has(id)) lane[id] = CENTER;
  });
  genKeys.forEach((g) => {
    const taken = new Set(gens[g].filter((id) => lane[id] != null).map((id) => lane[id]));
    const pending = gens[g].filter((id) => lane[id] == null);
    pending.sort((a, b) => {
      const pa = (parentsOf[a] || [])[0];
      const pb = (parentsOf[b] || [])[0];
      const la = pa != null && lane[pa] != null ? lane[pa] : CENTER;
      const lb = pb != null && lane[pb] != null ? lane[pb] : CENTER;
      return la - lb || a.localeCompare(b);
    });
    for (const id of pending) {
      const parentId = (parentsOf[id] || [])[0];
      const anchor = parentId != null && lane[parentId] != null ? lane[parentId] : CENTER;
      let chosen: number | null = null;
      for (let d = 1; d < LANES; d++) {
        const right = anchor + d;
        const left = anchor - d;
        if (right < LANES && !taken.has(right)) {
          chosen = right;
          break;
        }
        if (left >= 0 && !taken.has(left)) {
          chosen = left;
          break;
        }
      }
      if (chosen == null) {
        for (let l = 0; l < LANES; l++) {
          if (!taken.has(l)) {
            chosen = l;
            break;
          }
        }
        if (chosen == null) chosen = anchor;
      }
      lane[id] = chosen;
      taken.add(chosen);
    }
  });

  // ── Truncation detection → ghost stubs ──
  const topGen = genKeys[0];
  const botGen = genKeys[genKeys.length - 1];
  const topRow = gens[topGen] || [];
  const botRow = gens[botGen] || [];
  const upstreamTruncated = topGen < 0 && topRow.some((id) => (parentsOf[id] || []).some((p) => genOf[p] == null));
  const downstreamTruncated = botGen > 0 && botRow.some((id) => (childrenOf[id] || []).some((c) => genOf[c] == null));
  const upstreamHidden = upstreamTruncated
    ? new Set(topRow.flatMap((id) => (parentsOf[id] || []).filter((p) => genOf[p] == null))).size
    : 0;
  const downstreamHidden = downstreamTruncated
    ? new Set(botRow.flatMap((id) => (childrenOf[id] || []).filter((c) => genOf[c] == null))).size
    : 0;

  // ── Geometry ──
  const W = Math.max(availW, 240);
  const EDGE_PAD = 24;
  const LANE_W = Math.max(32, Math.min(54, (W - EDGE_PAD * 2) / LANES));
  const LEFT_PAD = (W - LANES * LANE_W) / 2;
  const NODE_R = 9;
  const SELECTED_R = 11;

  const GHOST_UP_H = upstreamTruncated ? 40 : 0;
  const GHOST_DOWN_H = downstreamTruncated ? 40 : 0;
  const TOP_PAD = 12 + GHOST_UP_H;
  const BOT_PAD = 16 + GHOST_DOWN_H;
  const H = Math.max(availH - 26, 360);

  const drawTop = TOP_PAD;
  const drawBottom = H - BOT_PAD;
  const drawH = drawBottom - drawTop;

  const selectedRowIdx = genKeys.indexOf(0);
  const nRows = genKeys.length;
  const rowsAbove = selectedRowIdx;
  const rowsBelow = nRows - 1 - selectedRowIdx;

  let anchorFrac: number;
  if (rowsAbove === 0 && rowsBelow === 0) anchorFrac = 0.5;
  else if (rowsAbove === 0) anchorFrac = 0.15;
  else if (rowsBelow === 0) anchorFrac = 0.92;
  else anchorFrac = 0.72;
  const anchorY = drawTop + drawH * anchorFrac;

  const MIN_ROW_H = 32;
  const MAX_ROW_H = 110;
  const rowHAbove = rowsAbove > 0 ? Math.max(MIN_ROW_H, Math.min(MAX_ROW_H, (anchorY - drawTop) / rowsAbove)) : 0;
  const rowHBelow = rowsBelow > 0 ? Math.max(MIN_ROW_H, Math.min(MAX_ROW_H, (drawBottom - anchorY) / rowsBelow)) : 0;

  type Pos = { x: number; y: number; gen: number; lane: number };
  const pos: Record<string, Pos> = {};
  genKeys.forEach((g, ri) => {
    let y: number;
    if (g === 0) y = anchorY;
    else if (g < 0) y = anchorY - (selectedRowIdx - ri) * rowHAbove;
    else y = anchorY + (ri - selectedRowIdx) * rowHBelow;
    for (const id of gens[g]) {
      const x = LEFT_PAD + lane[id] * LANE_W + LANE_W / 2;
      pos[id] = { x, y, gen: g, lane: lane[id] };
    }
  });

  const visibleIds = new Set(Object.keys(pos));
  const lineageEdges = edges.filter((e) => visibleIds.has(edgeId(e.source)) && visibleIds.has(edgeId(e.target)));
  const isTrunkEdge = (e: D3GraphEdge) => trunk.has(edgeId(e.source)) && trunk.has(edgeId(e.target));

  const selectedColor = typeColour(node?.type);

  // Header metrics
  const upstreamTotal = Object.values(genOf).filter((g) => g < 0).length;
  const downstreamTotal = Object.values(genOf).filter((g) => g > 0).length;
  const shownCount = Object.keys(pos).length;
  const collapsedCount = Object.values(rowOverflow).reduce((a, b) => a + b, 0) + upstreamHidden + downstreamHidden;
  const depth = trunk.size - 1;
  const isRootlessLeaf = upstreamTotal === 0 && downstreamTotal === 0;

  const connectorPath = (f: Pos, t: Pos) => {
    const dy = t.y - f.y;
    const fy = f.y + NODE_R;
    const ty = t.y - NODE_R;
    if (f.lane === t.lane) return `M ${f.x} ${fy} L ${f.x} ${ty}`;
    const midY = f.y + dy / 2;
    return `M ${f.x} ${fy} L ${f.x} ${midY} L ${t.x} ${midY} L ${t.x} ${ty}`;
  };

  // Per-shape area scaling so glyphs read at equal visual weight.
  const AREA_SCALE: Record<string, number> = {
    circle: 1.0,
    roundrect: 0.886,
    roundsquare: 0.886,
    diamond: 1.253,
    hex: 1.1,
    octagon: 1.04,
    triangle: 1.35,
    star: 1.25,
    shield: 1.0,
    pentagon: 1.15, // matches canvas/areaScale.ts √(π/2.378)
    cross: 1.1,
  };

  function shapeEl(shape: string, x: number, y: number, s: number, common: Record<string, unknown>) {
    if (shape === 'roundrect' || shape === 'roundsquare' || shape === 'shield')
      return <rect x={x - s} y={y - s} width={s * 2} height={s * 2} rx="2" {...common} />;
    if (shape === 'diamond')
      return <path d={`M ${x} ${y - s} L ${x + s} ${y} L ${x} ${y + s} L ${x - s} ${y} Z`} {...common} />;
    if (shape === 'hex' || shape === 'octagon') {
      const sides = shape === 'hex' ? 6 : 8;
      const pts: string[] = [];
      for (let i = 0; i < sides; i++) {
        const a = (i * 2 * Math.PI) / sides;
        pts.push(`${x + s * Math.cos(a)} ${y + s * Math.sin(a)}`);
      }
      return <path d={'M ' + pts.join(' L ') + ' Z'} {...common} />;
    }
    if (shape === 'triangle')
      return <path d={`M ${x} ${y - s} L ${x + s} ${y + s} L ${x - s} ${y + s} Z`} {...common} />;
    if (shape === 'pentagon') {
      // Investigation pentagon — same geometry as the canvas + rail
      // (sprint-236 GAP-1 shape parity).
      const pts: string[] = [];
      for (let i = 0; i < 5; i++) {
        const a = (i * 2 * Math.PI) / 5 - Math.PI / 2;
        pts.push(`${x + s * Math.cos(a)} ${y + s * Math.sin(a)}`);
      }
      return <path d={'M ' + pts.join(' L ') + ' Z'} {...common} />;
    }
    if (shape === 'star') {
      const pts: string[] = [];
      for (let i = 0; i < 10; i++) {
        const r = i % 2 === 0 ? s : s * 0.45;
        const a = (i * Math.PI) / 5 - Math.PI / 2;
        pts.push(`${x + r * Math.cos(a)} ${y + r * Math.sin(a)}`);
      }
      return <path d={'M ' + pts.join(' L ') + ' Z'} {...common} />;
    }
    return <circle cx={x} cy={y} r={s} {...common} />;
  }

  // Unique keyframe name so multiple cards don't collide.
  const pulseAnim = `mm-pulse-${nodeId.replace(/[^a-zA-Z0-9]/g, '')}`;

  return (
    <div ref={containerRef} className="w-full flex-1 min-h-0 flex flex-col" data-testid="lineage-minimap">
      <style>{`
        @keyframes ${pulseAnim} {
          0% { stroke-dashoffset: 24; opacity: 0; }
          15% { opacity: 0.95; }
          85% { opacity: 0.95; }
          100% { stroke-dashoffset: -120; opacity: 0; }
        }
      `}</style>

      {/* Header — dominant-path depth + shown/collapsed counts */}
      <div
        className="px-3.5 pt-1 pb-1.5 flex justify-between font-mono text-[9px] uppercase tracking-[0.1em] text-[var(--e-text-muted)]"
      >
        <span>{isRootlessLeaf ? 'root · unconnected' : `depth ${depth}`}</span>
        <span>
          {shownCount} shown
          {collapsedCount > 0 && <span className="text-[var(--e-text-muted)]/60"> · {collapsedCount} collapsed</span>}
        </span>
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        preserveAspectRatio="xMidYMid meet"
        className="block"
      >
        {/* Field grid — faint dots at lane × row intersections */}
        <g opacity="0.45">
          {(() => {
            const dotR = Math.max(0.6, Math.min(1.4, 14 / Math.sqrt(Object.keys(pos).length + 4)));
            const rowYs = new Set<number>();
            Object.values(pos).forEach((p) => rowYs.add(Math.round(p.y)));
            const sortedYs = [...rowYs].sort((a, b) => a - b);
            const fieldYs = new Set<number>(sortedYs);
            const gaps: number[] = [];
            for (let i = 1; i < sortedYs.length; i++) gaps.push(sortedYs[i] - sortedYs[i - 1]);
            const avgGap = gaps.length ? gaps.reduce((a, b) => a + b, 0) / gaps.length : 40;
            const topY = sortedYs[0] ?? drawTop;
            const botY = sortedYs[sortedYs.length - 1] ?? drawBottom;
            for (let y = topY - avgGap; y > 8; y -= avgGap) fieldYs.add(Math.round(y));
            for (let y = botY + avgGap; y < H - 8; y += avgGap) fieldYs.add(Math.round(y));
            const laneXs: number[] = [];
            for (let l = 0; l < LANES; l++) laneXs.push(LEFT_PAD + l * LANE_W + LANE_W / 2);
            const dots: ReactElement[] = [];
            let k = 0;
            [...fieldYs].forEach((y) => {
              laneXs.forEach((x) => {
                if (x < 6 || x > W - 6) return;
                dots.push(<circle key={'d' + k++} cx={x} cy={y} r={dotR} fill="var(--e-text-muted)" fillOpacity="0.4" />);
              });
            });
            return dots;
          })()}
        </g>

        {/* Ghost continuation — upstream */}
        {upstreamTruncated &&
          (() => {
            const stubTop = drawTop - GHOST_UP_H + 4;
            const stubBot = drawTop - 4;
            return (
              <g opacity="0.55">
                {topRow.map((id) => {
                  const p = pos[id];
                  if (!p) return null;
                  return (
                    <g key={'gu-' + id}>
                      <path
                        d={`M ${p.x} ${stubBot} L ${p.x} ${stubTop + 10}`}
                        stroke="var(--e-border-secondary)"
                        strokeWidth="1"
                        strokeDasharray="2 3"
                        fill="none"
                      />
                      <circle
                        cx={p.x}
                        cy={stubTop + 5}
                        r="3"
                        fill="var(--e-bg-sunken)"
                        stroke="var(--e-text-muted)"
                        strokeOpacity="0.35"
                        strokeWidth="1"
                      />
                    </g>
                  );
                })}
                {upstreamHidden > 0 && (
                  <text
                    x={W - 14}
                    y={stubTop + 8}
                    textAnchor="end"
                    className="font-mono"
                    fontSize="9"
                    fill="var(--e-text-muted)"
                    letterSpacing="0.08em"
                  >
                    +{upstreamHidden} more
                  </text>
                )}
              </g>
            );
          })()}

        {/* Branch connectors first (z-order under trunk) */}
        {lineageEdges
          .filter((e) => !isTrunkEdge(e))
          .map((e, i) => {
            const f = pos[edgeId(e.source)];
            const t = pos[edgeId(e.target)];
            if (!f || !t) return null;
            return (
              <path
                key={'be-' + i}
                d={connectorPath(f, t)}
                stroke="var(--e-border-secondary)"
                strokeOpacity={0.5}
                strokeWidth={1}
                fill="none"
              />
            );
          })}

        {/* Trunk connectors with travelling pulse */}
        {lineageEdges.filter(isTrunkEdge).map((e, i) => {
          const f = pos[edgeId(e.source)];
          const t = pos[edgeId(e.target)];
          if (!f || !t) return null;
          const d = connectorPath(f, t);
          return (
            <g key={'te-' + i}>
              <path d={d} stroke={selectedColor} strokeOpacity={0.75} strokeWidth={1.25} fill="none" />
              <path
                d={d}
                stroke={selectedColor}
                strokeOpacity={0.95}
                strokeWidth={2}
                strokeLinecap="round"
                fill="none"
                strokeDasharray="14 110"
                style={{ animation: `${pulseAnim} 2.6s linear infinite`, animationDelay: `${i * 80}ms` }}
              />
            </g>
          );
        })}

        {/* Nodes */}
        {Object.entries(pos).map(([id, p]) => {
          const n = nodes.find((x) => x.id === id);
          if (!n) return null;
          const isSelected = id === nodeId;
          const size = isSelected ? SELECTED_R : NODE_R;
          const shape = NODE_CLASSES[n.type]?.shape ?? 'circle';
          const status = n.status ?? 'idle';
          const s = size * (AREA_SCALE[shape] ?? 1.0);
          const common = {
            fill: statusFill(status),
            fillOpacity: status === 'idle' ? 0.45 : 0.9,
            stroke: typeColour(n.type),
            strokeOpacity: 0.9,
            strokeWidth: 1,
            style: { cursor: 'pointer' },
            onClick: () => onSelect?.(id),
          };
          return (
            <g key={id}>
              {isSelected && (
                <circle cx={p.x} cy={p.y} r={size + 4} fill="none" stroke={selectedColor} strokeOpacity="0.45" strokeWidth="1" />
              )}
              {shapeEl(shape, p.x, p.y, s, common)}
            </g>
          );
        })}

        {/* Per-row "+N" overflow badges */}
        {Object.entries(rowOverflow).map(([genStr, count]) => {
          const g = Number(genStr);
          const row = gens[g] || [];
          if (!row.length) return null;
          const firstPos = pos[row[0]];
          if (!firstPos) return null;
          const maxX = Math.max(...row.map((id) => pos[id]?.x || 0));
          const bx = Math.min(W - 18, maxX + LANE_W * 0.8);
          return (
            <g key={'ov-' + g}>
              <circle cx={bx} cy={firstPos.y} r="10" fill="var(--e-bg-sunken)" stroke="var(--e-border-secondary)" strokeWidth="1" />
              <text
                x={bx}
                y={firstPos.y + 3}
                textAnchor="middle"
                className="font-mono"
                fontSize="9"
                fill="var(--e-text-muted)"
                letterSpacing="0.04em"
              >
                +{count}
              </text>
            </g>
          );
        })}

        {/* Ghost continuation — downstream */}
        {downstreamTruncated &&
          (() => {
            const stubTop = drawBottom + 4;
            const stubBot = drawBottom + GHOST_DOWN_H - 4;
            return (
              <g opacity="0.55">
                {botRow.map((id) => {
                  const p = pos[id];
                  if (!p) return null;
                  return (
                    <g key={'gd-' + id}>
                      <path
                        d={`M ${p.x} ${stubTop} L ${p.x} ${stubBot - 10}`}
                        stroke="var(--e-border-secondary)"
                        strokeWidth="1"
                        strokeDasharray="2 3"
                        fill="none"
                      />
                      <circle
                        cx={p.x}
                        cy={stubBot - 5}
                        r="3"
                        fill="var(--e-bg-sunken)"
                        stroke="var(--e-text-muted)"
                        strokeOpacity="0.35"
                        strokeWidth="1"
                      />
                    </g>
                  );
                })}
                {downstreamHidden > 0 && (
                  <text
                    x={W - 14}
                    y={stubBot - 2}
                    textAnchor="end"
                    className="font-mono"
                    fontSize="9"
                    fill="var(--e-text-muted)"
                    letterSpacing="0.08em"
                  >
                    +{downstreamHidden} more
                  </text>
                )}
              </g>
            );
          })()}
      </svg>
    </div>
  );
}
